import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../common/types';
import type { InboxAttachment } from '../outreachTypes';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AttachmentChipsProps {
  attachments: InboxAttachment[];
  /** The answer text: a chip whose line is in it shows as added. */
  body: string;
  disabled: boolean;
  onAdd: (line: string) => void;
}

/**
 * The one-click attachments of an answer (design outreach-v2 m1): links, never files. Each chip
 * adds its line to the answer. A link that the song does not have yet shows no chip.
 */
const AttachmentChipsComponent: React.FC<AttachmentChipsProps> = ({ attachments, body, disabled, onAdd }) => {
  const intl = useIntl();
  const chips = useMemo(
    () =>
      attachments
        .filter((attachment) => attachment.available && attachment.line !== '')
        .map((attachment) => {
          const added = body.includes(attachment.line);
          return (
            <button
              key={attachment.kind}
              type="button"
              className={clsx(styles.attach, added && styles.attachOn)}
              disabled={disabled || added}
              aria-pressed={added}
              onClick={() => onAdd(attachment.line)}
              data-testid={`attachment-${attachment.kind}`}
            >
              <FontAwesomeIcon icon={added ? faCheck : faPlus} />
              {attachment.label}
            </button>
          );
        }),
    [attachments, body, disabled, onAdd]
  );
  if (chips.length === 0) {
    return null;
  }
  return (
    <div className={styles.attachRow} role="group" aria-label={intl.formatMessage(messages.attachments)}>
      {chips}
    </div>
  );
};

export const AttachmentChips = TypedMemo(AttachmentChipsComponent);
